<template>
    <b-container fluid class="overview">
        <h1 class="text-left">{{ $t('menuItemThemeLayers') }}</h1>
        <br />
        <b-row>
            <!-- Search field -->
            <search-box
                id="searchBackgroundLayers"
                @input="$_onInput"
                :info="$t('searchInfo')"
                :autofocus="true"
                :value="searchText"
                :saveInState="true"
            >
            </search-box>
            <!-- Background layer filter field -->
            <div class="col-md-2 reset-left">
                <x-select
                    :id="`selectBackgroundLayers`"
                    :name="`selectBackgroundLayers`"
                    @selected="$_onSelectBackgroundLayerFilter"
                    :options="backgroundLayerFilterOptions"
                    :textDefaultOption="$t('typeBackgroundLayerFilter')"
                    :value="selectedBackgroundLayerFilter"
                    :simpleMode="true"
                    :saveInState="true"
                >
                </x-select>
            </div>
            <!-- Ows filter field -->
            <div class="col-md-3 reset-left">
                <x-select
                    :id="`ddOws`"
                    :name="`ddOws`"
                    :options="
                        $_convertToValueTextPair(owsRecords, 'name', 'name')
                    "
                    :textDefaultOption="$t('owsFilter')"
                    v-model="selectedOws"
                    :saveInState="true"
                >
                </x-select>
                <span
                    v-show="ows != null && ows.unavailable"
                    class="input-group-addon"
                    :title="$t('owsNotAvailable')"
                >
                    <em class="glyphicon glyphicon-alert"></em>
                </span>
            </div>
            <!-- Service type filter field -->
            <x-select
                :id="`themeLayerServiceType`"
                :name="`themeLayerServiceType`"
                @selected="$_onServiceType"
                :options="$_owsServiceTypeOptions"
                :textDefaultOption="$t('typeFilter')"
                :value="selectedServiceType"
                :saveInState="true"
            >
            </x-select>
            <b-link class="px-3 pt-2" @click="$_clearFilters()">
                {{ $t('clearFilters') }}
            </b-link>
        </b-row>
        <!-- Overview Table -->
        <b-table
            id="themelayeroverview"
            striped
            hover
            small
            outlined
            bordered
            v-show="dataLoaded"
            @row-clicked="$_onDetails"
            :tbody-tr-class="$_rowClass"
            :items="filteredRecords"
            :fields="fields"
            :sort-by.sync="sortBy"
            sort-icon-left
            ><!-- Scoped slot for checkbox column Active-->
            <template #cell(active)="data">
                <div class="text-center">
                    <b-form-checkbox
                        v-model="data.item.active"
                        @change="$_changeActiveStatus(data.item)"
                    ></b-form-checkbox>
                </div>
            </template>
            <template v-slot:cell(isUnavailable)="data">
                <em
                    title="themalaag niet beschikbaar"
                    class="error"
                    :class="data.value"
                ></em>
            </template>
        </b-table>
        <!-- No content for overview -->
        <b-row>
            <b-col md="12" v-show="!dataLoaded">
                <span>{{ $t('themeLayersNotPresent') }}</span>
            </b-col>
        </b-row>
        <router-links-footer :routerLinks="[{ name: 'themeLayerDetailNew' }]" />
    </b-container>
</template>

<script>
    import SearchBox from '../../components/SearchBox.vue';
    import XSelect from '../../components/BaseComponents/x-select.vue';
    import RouterLinksFooter from '../../components/dashboard/RouterLinksFooter.vue';
    import { OWS_TYPE } from '../../constants';

    import {
        convertToValueTextPair,
        owsServiceTypeOptions,
        nprogress
    } from '../../services/utilities';

    export default {
        name: 'ThemeLayerOverview',

        components: {
            'search-box': SearchBox,
            'x-select': XSelect,
            'router-links-footer': RouterLinksFooter
        },

        data: function () {
            return {
                fields: [
                    {
                        key: 'active',
                        label: this.$t('columnNameActive'),
                        sortable: true,
                        class: 'text-center', // Center header text
                        thStyle: { width: '1%' } // Narrow column
                    },
                    {
                        key: 'title',
                        label: this.$t('columnNameTitle'),
                        sortable: true,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'baselayer',
                        label: this.$t('labelThemeLayerBaseLayer'),
                        sortable: true,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'source.name',
                        label: this.$t('columnOwsTitle'),
                        sortable: true,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'serviceType',
                        label: this.$t('columnTypeTitle'),
                        sortable: true,
                        thStyle: { border: 0 }
                    },
                    {
                        key: 'isUnavailable',
                        label: '',
                        sortable: false,
                        class: 'text-right',
                        thStyle: { border: 0 }
                    }
                ],
                sortBy: 'title',
                records: [],
                owsRecords: [],
                searchText: '',
                selectedOws: null,
                ows: null,
                selectedServiceType: null,
                selectedBackgroundLayerFilter: null,
                backgroundLayerFilterOptions: [
                    { value: true, text: this.$t('optionOnlyBackgroundLayers') },
                    { value: false, text: this.$t('optionOnlyNonBackgroundLayers') }
                ]
            };
        },

        computed: {
            $_owsServiceTypeOptions() {
                return owsServiceTypeOptions;
            },

            hasSearchValue() {
                return this.searchText && this.searchText.length > 0;
            },

            dataLoaded() {
                return this.records.length > 0;
            },

            filteredRecords() {
                let records = this.records;
                // filter records on NAME
                if (this.searchText && this.searchText.length > 0) {
                    const tokens = this.searchText.toLowerCase();
                    records = records.filter((record) => {
                        return record.title.toLowerCase().includes(tokens);
                    });
                }
                // filter records on OWS
                if (this.selectedOws && this.selectedOws.length > 0) {
                    records = records.filter((record) => {
                        return record.source.name === this.selectedOws;
                    });
                }
                // filter records on service type
                if (
                    this.selectedServiceType &&
                    this.selectedServiceType.length > 0
                ) {
                    records = records.filter((record) => {
                        return record.serviceType.includes(
                            this.selectedServiceType
                        );
                    });
                }
                // filter records on baseLayer
                if (this.selectedBackgroundLayerFilter !== null) {
                    if (this.selectedBackgroundLayerFilter) {
                        records = records.filter((record) => {
                            return record.baselayer === this.$t('btnTitleYes');
                        });
                    } else {
                        records = records.filter((record) => {
                            return record.baselayer === '';
                        });
                    }
                }
                // set error icons
                records.forEach((record) => {
                    if (record.unavailable && record.serviceType != OWS_TYPE.OGCT) {
                        record.isUnavailable = 'fas fa-exclamation-triangle';
                    } else {
                        record.isUnavailable = null;
                    }
                });
                return records;
            }
        },

        async beforeCreate() {
            nprogress.start();
            // get all ows data from the backend
            let result = await this.$store.dispatch('useApiData', {
                name: 'GetOwsOverview'
            });
            this.owsRecords = result?.data;
            // get all theme layer data from the backend
            result = await this.$store.dispatch('useApiData', {
                name: 'GetThemeLayersOverview'
            });
            this.records = result?.data;
            nprogress.done();
            // get list with all ows names
            this.records = this.records.map((record) => {
                // set baselayer property name
                if (record.baselayer) {
                    record.baselayer = this.$t('btnTitleYes');
                } else {
                    record.baselayer = '';
                }
                return record;
            });
        },

        methods: {
            $_convertToValueTextPair(inputArray, key, value) {
                return convertToValueTextPair(inputArray, key, value);
            },

            $_onDetails(record) {
                this.$router.push({
                    path: `/dashboard/themelayer/detail/${record.id}`
                });
            },

            $_onInput(searchText) {
                this.searchText = searchText;
            },

            $_onServiceType(input) {
                this.selectedServiceType = input;
            },

            $_rowClass() {
                return 'tablerow';
            },
            $_onSelectBackgroundLayerFilter(input) {
                this.selectedBackgroundLayerFilter = input;
            },
            $_clearFilters() {
                this.$_onInput('');
                this.$_onSelectBackgroundLayerFilter('');
                this.selectedOws = null;
                this.$_onServiceType(null);
            },
            $_changeActiveStatus(data) {
                // Write active state to database
                return this.$store.dispatch('useApiData', {
                    name: 'UpdateThemeLayerActiveState',
                    data: { id: data.id, active: data.active }
                });
            }
        }
    };
</script>

<style>
.error {
    color: red;
}
/*Following classes together remove the vertical line
        between the last two cells in each row
    */
#themelayeroverview.table-bordered td:nth-child(4) {
    border-right-width: 0px;
}

#themelayeroverview.table-bordered td:last-child {
    border-left-width: 0px;
}

.reset-left {
    padding-left: 1px !important;
}
</style>
